import { useMemo, useState } from 'react';
import { BARTER_PRICES, TRAVEL_GEAR } from '../game/constants.js';
import './PawnShopScene.css';

const money = (value) => `$${Math.max(0, Math.floor(Number(value) || 0)).toLocaleString()}`;

function PawnChoice({ children, detail, disabled, onClick, tone = '' }) {
  return (
    <button className={`pawn-scene-choice ${tone}`.trim()} disabled={disabled} onClick={onClick}>
      <strong>{children}</strong>
      {detail && <small>{detail}</small>}
    </button>
  );
}

export function PawnShopScene({ gameState, busy, actions, onDismiss }) {
  const [screen, setScreen] = useState('main');
  const [dismissed, setDismissed] = useState(false);
  const cash = Number(gameState.cash || 0);
  const rep = Number(gameState.streetRep || 0);
  const stock = gameState.pawnTravelStock || {};
  const ownedGear = gameState.travelGear || {};
  const junk = useMemo(
    () => Object.entries(gameState.barterInventory || {}).filter(([, count]) => Number(count) > 0),
    [gameState.barterInventory],
  );

  if (dismissed && !onDismiss) return null;

  let title = "Wally's Pawn";
  let copy = "The old man behind the counter buys, sells, trades, and changes his mind whenever it becomes profitable. Everything is cash only and nothing has a believable history.";
  let choices = (
    <>
      <PawnChoice
        disabled={busy}
        onClick={() => setScreen('gear')}
        detail="Skateboards, bicycles, and transportation nobody has reported missing yet"
      >
        Check questionable transportation
      </PawnChoice>
      <PawnChoice
        disabled={busy || junk.length === 0}
        onClick={() => setScreen('junk')}
        detail={junk.length ? `${junk.reduce((sum, [, count]) => sum + Number(count), 0)} pawnable item(s) on you` : 'You have no suspicious junk to sell'}
      >
        Fence junk and electronics
      </PawnChoice>
      <PawnChoice
        disabled={busy}
        onClick={() => setScreen('look')}
        detail="Browse the glass counter and pretend none of it was stolen"
      >
        Look around the shop
      </PawnChoice>
      <PawnChoice
        disabled={busy}
        onClick={() => { if (onDismiss) onDismiss(); else setDismissed(true); }}
        detail="Step away from the counter and choose your next destination"
        tone="leave"
      >
        Leave the Pawn Shop counter
      </PawnChoice>
    </>
  );

  if (screen === 'gear') {
    title = 'Questionable Transportation';
    copy = `The transportation stock was rolled once for day ${Number(stock.day || gameState.day || 1)}. Reloading the page will not make another bicycle materialize behind the building.`;
    choices = (
      <>
        {Object.entries(TRAVEL_GEAR).map(([itemId, item]) => {
          const itemStock = stock[itemId] || {};
          const owned = Boolean(ownedGear[itemId]?.owned);
          const available = Boolean(itemStock.available) && !owned;
          const price = Number(itemStock.price || 0);
          const detail = owned
            ? `Owned · ${Math.round((1 - item.debtGrowthFactor) * 100)}% less travel-debt growth`
            : available
              ? `${money(price)} · ${Math.round((1 - item.debtGrowthFactor) * 100)}% less debt growth · street incidents ×${item.encounterMultiplier.toFixed(2)}`
              : 'Not in stock today';
          return (
            <PawnChoice
              key={itemId}
              disabled={busy || owned || !available || cash < price}
              onClick={() => actions.buyTravelGear(itemId)}
              detail={cash < price && available ? `${detail} · you need ${money(price - cash)} more` : detail}
              tone={available && cash >= price ? 'buy' : ''}
            >
              {owned ? `${item.name} — already owned` : available ? `Buy ${item.name}` : item.name}
            </PawnChoice>
          );
        })}
        <PawnChoice disabled={busy} onClick={() => setScreen('main')}>Back to the counter</PawnChoice>
      </>
    );
  } else if (screen === 'junk') {
    title = 'Cash for Other People’s Problems';
    copy = 'The pawn broker checks serial numbers only when somebody official is watching. Values shown are estimates; hot goods can still turn a simple sale into a very bad afternoon.';
    choices = (
      <>
        {junk.map(([itemId, count]) => (
          <PawnChoice
            key={itemId}
            disabled={busy || Number(count) <= 0}
            onClick={() => actions.sellBarter(itemId)}
            detail={`${count} owned · estimated ${money(BARTER_PRICES[itemId] || 0)} each`}
            tone="sell"
          >
            Sell {itemId.replaceAll('_', ' ')}
          </PawnChoice>
        ))}
        {junk.length === 0 && <p className="pawn-scene-empty">Nothing pawnable remains. Even the broker has standards, although nobody has located them.</p>}
        <PawnChoice disabled={busy} onClick={() => setScreen('main')}>Back to the counter</PawnChoice>
      </>
    );
  } else if (screen === 'look') {
    title = 'Behind the Glass';
    copy = 'A camera, a handgun, watches, an answering machine, and a VHS tape labeled “BE KIND REWIND” sit behind scratched glass. None are currently part of the live inventory, but the broker insists every one is collectible.';
    choices = (
      <>
        <div className="pawn-scene-flavor-grid" aria-label="Pawn Shop display case">
          <span><b>Old camera</b><small>$29 · lens cap missing since 1994</small></span>
          <span><b>Two watches</b><small>$18 / $35 · both disagree about the time</small></span>
          <span><b>Handgun</b><small>$199 · DISPLAY ONLY</small></span>
          <span><b>VHS tape</b><small>$7 · nobody remembers what is on it</small></span>
        </div>
        <PawnChoice disabled={busy} onClick={() => setScreen('main')}>Back to the counter</PawnChoice>
      </>
    );
  }

  return (
    <div className="pawn-scene-overlay" role="dialog" aria-modal="true" aria-labelledby="pawn-scene-title">
      <div className="pawn-scene-frame">
        <div className="pawn-scene-art-wrap">
          <img className="pawn-scene-art" src="./scenes/pawn-shop.webp" alt="The cluttered interior of Wally's grimy Pawn Shop" />
          <div className="pawn-scene-art-shade" aria-hidden="true" />
          <div className="pawn-scene-label">REDDING · CASH TALKS</div>
          <div className="pawn-scene-wallet">CASH <strong>{money(cash)}</strong> · REP <strong>{rep}</strong></div>
        </div>
        <div className="pawn-scene-lower">
          <section className="pawn-scene-copy">
            <span className="scene-kicker">BUY · SELL · TRADE · MAYBE</span>
            <h2 id="pawn-scene-title">{title}</h2>
            <p>{copy}</p>
            <blockquote>“If it ain’t stolen, it ain’t here.”</blockquote>
          </section>
          <section className="pawn-scene-choices" aria-label="Pawn Shop actions">{choices}</section>
        </div>
        {busy && <div className="pawn-scene-busy">THE BROKER IS RECOUNTING THE CASH…</div>}
      </div>
    </div>
  );
}
